/** Feuille de calcul collaborative (grille Yjs). */
import { el } from '../ui/dom.js';
import { icon } from '../ui/icons.js';
import { Drive } from '../api.js';
import { connectDoc, renderPresence } from '../collab.js';
import { shareModal } from './drive.js';

const COLS = 26;
const ROWS = 60;
const colName = (i) => String.fromCharCode(65 + i);

export function renderSheets(ctx) {
  const { content, params, user, navigate } = ctx;
  const nodeId = params.id;
  let provider, doc, stopPresence, saveTimer, destroyed = false;

  content.style.padding = '0';
  const root = el('div', { class: 'editor-wrap' });
  content.appendChild(root);

  (async () => {
    let node;
    try { ({ node } = await Drive.node(nodeId)); }
    catch (e) { root.appendChild(el('div', { class: 'empty' }, el('h3', {}, 'Feuille introuvable'), el('div', {}, e.message))); return; }
    if (destroyed) return;
    const readOnly = node.role === 'viewer';
    ctx.setCrumbs([{ label: 'Mon Drive', path: '/drive' }, { label: node.name }]);

    const conn = connectDoc(nodeId, user); doc = conn.doc; provider = conn.provider;
    const cells = doc.getMap('cells');
    const ytitle = doc.getText('title');

    // Chrome
    const saveState = el('div', { class: 'save-state' }, el('span', { class: 'dot' }), el('span', {}, 'Connexion…'));
    const presence = el('div', { class: 'presence' });
    const titleInput = el('input', { class: 'input', style: { maxWidth: '320px', fontWeight: 600, border: 'none', background: 'transparent', fontSize: '16px' }, value: node.name });
    titleInput.disabled = readOnly;
    const back = el('button', { class: 'btn btn-sm btn-ghost', html: icon('back', 16), title: 'Drive', onclick: () => navigate('/drive') });
    const share = el('button', { class: 'btn btn-sm', html: icon('share', 16) + 'Partager', onclick: () => shareModal(node) });
    root.appendChild(el('div', { style: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 18px', borderBottom: '1px solid var(--hairline)' } },
      back, titleInput, saveState, el('div', { style: { flex: 1 } }), presence, node.role === 'owner' ? share : ''));

    // Grille
    const table = el('table', { class: 'sheet' });
    const thead = el('tr', {}, el('th', { class: 'rowh' }));
    for (let c = 0; c < COLS; c++) thead.appendChild(el('th', {}, colName(c)));
    table.appendChild(thead);

    const inputs = new Map(); // key -> input
    for (let r = 0; r < ROWS; r++) {
      const tr = el('tr', {}, el('td', { class: 'rowh' }, String(r + 1)));
      for (let c = 0; c < COLS; c++) {
        const key = r + ':' + c;
        const inp = el('input', { 'data-key': key, autocomplete: 'off', spellcheck: 'false' });
        inp.disabled = readOnly;
        inp.addEventListener('input', () => { doc.transact(() => cells.set(key, inp.value)); });
        inp.addEventListener('keydown', (e) => moveFocus(e, r, c, inputs));
        inputs.set(key, inp);
        tr.appendChild(el('td', {}, inp));
      }
      table.appendChild(tr);
    }
    root.appendChild(el('div', { class: 'sheet-wrap' }, table));

    const paint = () => { cells.forEach((v, k) => { const i = inputs.get(k); if (i && i.value !== v && document.activeElement !== i) i.value = v; }); };
    provider.once('synced', paint);
    cells.observe((ev) => { ev.keysChanged.forEach((k) => { const i = inputs.get(k); const v = cells.get(k) || ''; if (i && document.activeElement !== i) i.value = v; }); });

    // Titre
    provider.once('synced', () => { if (ytitle.length === 0 && node.name) doc.transact(() => ytitle.insert(0, node.name)); if (document.activeElement !== titleInput) titleInput.value = ytitle.toString() || node.name; });
    ytitle.observe(() => { if (document.activeElement !== titleInput) titleInput.value = ytitle.toString(); });
    if (!readOnly) titleInput.addEventListener('input', () => {
      const v = titleInput.value; doc.transact(() => { ytitle.delete(0, ytitle.length); ytitle.insert(0, v); });
      clearTimeout(saveTimer); saveTimer = setTimeout(() => Drive.update(nodeId, { name: v || 'Sans titre' }).catch(() => {}), 700);
    });

    stopPresence = renderPresence(presence, provider, user.id);
    const setState = (t, s) => { saveState.classList.toggle('syncing', !!s); saveState.lastChild.textContent = t; };
    provider.on('status', (e) => setState(e.status === 'connected' ? 'Enregistré dans JHN' : 'Reconnexion…', e.status !== 'connected'));
    let tt; doc.on('update', () => { setState('Synchronisation…', true); clearTimeout(tt); tt = setTimeout(() => setState('Enregistré dans JHN', false), 900); });
  })();

  return () => {
    destroyed = true; content.style.padding = ''; clearTimeout(saveTimer);
    try { stopPresence && stopPresence(); } catch {}
    try { provider && provider.destroy(); } catch {}
    try { doc && doc.destroy(); } catch {}
  };
}

function moveFocus(e, r, c, inputs) {
  let nr = r, nc = c;
  if (e.key === 'Enter' || e.key === 'ArrowDown') nr++;
  else if (e.key === 'ArrowUp') nr--;
  else if (e.key === 'ArrowRight' && e.target.selectionStart === e.target.value.length) nc++;
  else if (e.key === 'ArrowLeft' && e.target.selectionStart === 0) nc--;
  else if (e.key === 'Tab') { e.preventDefault(); nc += e.shiftKey ? -1 : 1; }
  else return;
  const next = inputs.get(nr + ':' + nc);
  if (next) { e.preventDefault(); next.focus(); next.select(); }
}
